function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useCallback, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import IsNotSaved from '../IsNotSaved';
import useUpdateState from '../hooks/useUpdateState';
import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import omit from 'lodash.omit';
import { DecimalFormFieldRow, IntegerFormFieldRow } from '@rd-web-markets/shared/dist/util/form';
import generalRouteSubRouteService from '@rd-web-markets/shared/dist/services/general_route_subroute.service';
const WAGE_COST_FIELDS = ['num_employees_in_enterprise_income_tax_is_deducted', 'num_employees_with_no_tax_deduction_due_to_dta', 'gross_wages_of_employees_for_whom_income_tax_is_deducted', 'gross_wages_employees_with_no_income_tax_due_to_dta', 'expenditure_to_secure_the_future_of_employees', 'num_employees_tax_deducted_by_full_time_eq', 'num_employees_full_time_eq_heads', 'num_employees_with_no_tax_deduction_due_to_dta_by_full_time_eq', 'gross_wages_full_time_eq_heads', 'gross_wages_employees_in_rd_projects_with_tax_deduct', 'gross_wages_employees_with_no_income_tax_in_rd_due_to_dta', 'expenditure_to_safeguard_the_future_of_employees_engaged_in_r_d'];
const INDENTED_WAGE_COST_FIELDS = ['num_employees_full_time_eq_heads', 'gross_wages_full_time_eq_heads'];
const WAGE_COST_FIELDS_WITH_INTEGER_VALUES = ['gross_wages_of_employees_for_whom_income_tax_is_deducted', 'gross_wages_employees_with_no_income_tax_due_to_dta', 'expenditure_to_secure_the_future_of_employees', 'num_employees_full_time_eq_heads', 'gross_wages_full_time_eq_heads', 'gross_wages_employees_with_no_income_tax_in_rd_due_to_dta', 'gross_wages_employees_in_rd_projects_with_tax_deduct', 'expenditure_to_safeguard_the_future_of_employees_engaged_in_r_d'];
const VERSION_8_FIELDS = ['num_employees_full_time_eq_heads', 'gross_wages_full_time_eq_heads'];
const WageCost = _ref => {
  let {
    financialYear,
    wageCosts,
    setWageCosts,
    elster
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isNotSaved, setIsNotSaved] = useState(false);
  const [updateState] = useUpdateState(setWageCosts, setIsNotSaved);
  const saveData = useErrorHandling(useCallback(async body => {
    const fieldValues = omit(body, ['id', 'elster_id', 'created_at', 'updated_at']);
    if (Object.values(fieldValues).some(value => value < 0)) {
      throw new Error('Values must be greater than 0');
    }
    const response = await generalRouteSubRouteService.update(body.elster_id, 'elsters', 'wage_cost', '', body);
    setWageCosts(_objectSpread({}, response));
    setIsNotSaved(false);
  }, [setWageCosts]));
  const renderDecimalInput = (fieldName, value) => /*#__PURE__*/React.createElement(DecimalFormFieldRow, {
    fieldName: fieldName,
    value: value,
    onChange: e => updateState({
      target: {
        value: e.target.value
      }
    }, wageCosts, fieldName),
    dataTestId: "wage-cost-decimal-".concat(fieldName)
  });
  const renderIntegerInput = (fieldName, value) => /*#__PURE__*/React.createElement(IntegerFormFieldRow, {
    fieldName: fieldName,
    value: value,
    onChange: e => updateState({
      target: {
        value: e.target.value
      }
    }, wageCosts, fieldName),
    dataTestId: "wage-cost-integer-".concat(fieldName)
  });
  const topPageTitle = /*#__PURE__*/React.createElement(Row, {
    className: "mb-5"
  }, /*#__PURE__*/React.createElement(Col, {
    md: 7,
    style: {
      fontWeight: 'bold'
    }
  }, t('information_on_total_wage_costs_in_the_enterprise')), /*#__PURE__*/React.createElement(Col, {
    md: 5
  }));
  const middlePageTitle = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Row, {
    className: "mb-5"
  }, /*#__PURE__*/React.createElement(Col, {
    md: 7,
    style: {
      fontWeight: 'bold'
    }
  }, t('information_on_total_wage_costs_of_employees_engaged_in_r_d_in_the_enterprise')), /*#__PURE__*/React.createElement(Col, {
    md: 5
  })));

  // If version_8: render all fields (including VERSION_8_FIELDS). Otherwise: render everything except VERSION_8_FIELDS.
  const isVersion8 = (elster === null || elster === void 0 ? void 0 : elster.version_number) === 'version_8';
  const fieldsToRender = isVersion8 ? WAGE_COST_FIELDS : WAGE_COST_FIELDS.filter(key => !VERSION_8_FIELDS.includes(key));
  const costRows = fieldsToRender.map(key => /*#__PURE__*/React.createElement("div", {
    key: key + 'wageCost'
  }, key === 'num_employees_in_enterprise_income_tax_is_deducted' && topPageTitle, key === 'num_employees_tax_deducted_by_full_time_eq' && middlePageTitle, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 7
  }, /*#__PURE__*/React.createElement("p", {
    style: {
      marginLeft: INDENTED_WAGE_COST_FIELDS.includes(key) ? '30px' : '0px'
    }
  }, t(key))), /*#__PURE__*/React.createElement(Col, {
    md: 1
  }, WAGE_COST_FIELDS_WITH_INTEGER_VALUES.includes(key) ? renderIntegerInput(key, wageCosts[key]) : renderDecimalInput(key, wageCosts[key])))));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 7
  }, t('financial_year')), /*#__PURE__*/React.createElement(Col, {
    md: 5
  }, financialYear)), /*#__PURE__*/React.createElement("hr", null), costRows, /*#__PURE__*/React.createElement(IsNotSaved, {
    isNotSaved: isNotSaved,
    save: saveData,
    paramsForUpdate: [wageCosts]
  }));
};
export default WageCost;